import {
  Component,
  createContext,
  createEffect,
  createSignal,
  JSXElement,
  mergeProps,
  Show,
  useContext
} from 'solid-js';
import { useQuery } from '@tanstack/solid-query';
import {
  getActiveAuthState,
  getCurrentAuthStateQueryKey,
  isApiError,
  retryAuthorizedRequests
} from '~/api';
import { timeSignatures } from '~/consts.ts';
import { usePreviewModeContext } from '~/components/AppData/PreviewModeContext.tsx';
import { AuthRequestStates } from '~/api/types/hostedAuthData.ts';
import { takeActionBasedOnAuthState } from '~/pages/ExternalPages/commons/utils.tsx';
import SkLoader from '~/components/ui/SkLoader';

type HostedAuthPagesSessionContextProps = {
  fetchOnLoad?: boolean;
  children: JSXElement;
};

export type HostedAuthStateValidatorContextValueType = {
  refetchState: () => Promise<void>;
  authState: AuthRequestStates | null;
};

const defaultContextValue: HostedAuthStateValidatorContextValueType = {
  refetchState: async () => {},
  authState: null
};

const HostedAuthStateValidatorContext = createContext(defaultContextValue);

function HostedAuthStateValidator(
  props: HostedAuthPagesSessionContextProps
): JSXElement {
  const merged = mergeProps(
    {
      fetchOnLoad: true
    },
    props
  );
  const previewModeContext = usePreviewModeContext();
  const [authState, setAuthState] = createSignal<AuthRequestStates | null>(
    null
  );
  const [shouldRenderChildren, setShouldRenderChildren] = createSignal(false);

  const activeAuthStateQuery = useQuery(() => ({
    queryKey: getCurrentAuthStateQueryKey(),
    queryFn: async () => {
      // Session validation api should not get called in Preview Mode
      // Note: The same thing could have been achieved by
      // disabling the query and refetching in non-preview mode
      // but with that implementation the refetchOnWindowFocus is not happening
      // because the query will be disabled by default
      return previewModeContext.isPreviewModeEnabled
        ? null
        : await getActiveAuthState();
    },
    retry: retryAuthorizedRequests,
    enabled: merged.fetchOnLoad,
    refetchOnWindowFocus: true,
    staleTime: timeSignatures.SEC_IN_MILLIS
  }));

  createEffect(() => {
    // Redirection should not happen in previewMode
    if (previewModeContext.isPreviewModeEnabled) {
      return;
    }
    let fetchedAuthState: AuthRequestStates | null = null;
    if (activeAuthStateQuery.isSuccess && activeAuthStateQuery.data) {
      fetchedAuthState = activeAuthStateQuery.data;
    } else if (
      activeAuthStateQuery.isError &&
      activeAuthStateQuery.error &&
      isApiError(activeAuthStateQuery.error)
    ) {
      fetchedAuthState = activeAuthStateQuery.error.error?.details?.[0]
        ?.error_code as AuthRequestStates;
    }
    setAuthState(fetchedAuthState);
    if (fetchedAuthState) {
      const navigatedToDiffRoute = takeActionBasedOnAuthState({
        authState: fetchedAuthState
      });
      setShouldRenderChildren(!navigatedToDiffRoute);
    }
  });

  return (
    <HostedAuthStateValidatorContext.Provider
      value={{
        refetchState: async () => {
          await activeAuthStateQuery.refetch();
        },
        authState: authState()
      }}
    >
      <Show
        when={shouldRenderChildren()}
        fallback={<SkLoader />}
      >
        {merged.children}
      </Show>
    </HostedAuthStateValidatorContext.Provider>
  );
}

function withHostedAuthState<T extends object>(
  component: Component<T>,
  options?: {
    fetchOnLoad?: boolean;
  }
) {
  const Renderer = component;
  return (props: T) => (
    <HostedAuthStateValidator fetchOnLoad={options?.fetchOnLoad ?? true}>
      <Renderer {...props} />
    </HostedAuthStateValidator>
  );
}

const useHostedAuthStateValidatorContext = () =>
  useContext(
    HostedAuthStateValidatorContext
  ) as HostedAuthStateValidatorContextValueType;

export { withHostedAuthState, useHostedAuthStateValidatorContext };
